import { ScaleVisualization } from '~src/interfaces/scale.interface';
import { translateContent } from '~src/services/translationServices';
import StarsList, { ListClass } from '~src/shared/features/scale/starsList/StarsList';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLocalesI } from '~src/interfaces/session.types';
import {
  LabelContent,
  LabelDisplay,
  LabelRatingContainer,
  LabelWrapper,
  McqAnchorStyled,
  McqAnswerContainer,
  NpsRatingContainer,
} from './DisplayScale.sc';

interface Props {
  selectedLabelIndex: number | number[];
  type: ScaleVisualization;
  labels: ContentLocalesI[];
  labelColor?: string;
  maxRating: number;
  onSmallHeader?: boolean;
  rating?: number | number[];
  small?: boolean;
  blank?: boolean;
  onlyText?: boolean;
  // Maximum number of options that should be visible by default. 'Show more' button is shown when
  // this is less than labelIndex.length
  maxVisibleOptions?: number;
}

function DisplayScale(props: Props) {
  const [t] = useTranslation();
  const selectedAnswerIndex = Array.isArray(props.selectedLabelIndex)
    ? props.selectedLabelIndex
    : [props.selectedLabelIndex];
  const [visibleOptions, setVisibleOptions] = useState(selectedAnswerIndex.slice(0, props.maxVisibleOptions));

  const canViewMore = props.maxVisibleOptions && selectedAnswerIndex.length > props.maxVisibleOptions;

  if (selectedAnswerIndex.length < 1) return null;

  const showHideVisibleOptions = () => {
    if (selectedAnswerIndex.length > 1 && visibleOptions.length !== selectedAnswerIndex.length) {
      setVisibleOptions(selectedAnswerIndex);
    }
    if (selectedAnswerIndex.length > 1 && visibleOptions.length === selectedAnswerIndex.length) {
      setVisibleOptions(selectedAnswerIndex.slice(0, 1));
    }
  };

  let scale = null;

  // STARS
  if (props.type === ScaleVisualization.Stars) {
    scale = (
      <StarsList
        blank={props.blank}
        listCls={ListClass.coloredList}
        ratingIndex={selectedAnswerIndex[0]}
      />
    );
  }
  // NPS
  if (props.type === ScaleVisualization.Nps) {
    scale = (
      <NpsRatingContainer
        $blank={props.blank}
        $empty={props.rating === null}
        $rating={selectedAnswerIndex[0]}
      >
        <span>{props.rating !== null ? props.selectedLabelIndex : '-'}</span>
      </NpsRatingContainer>
    );
  }
  // LABELS
  if (props.type === ScaleVisualization.Labels) {
    const labelContent = translateContent(props.labels[selectedAnswerIndex[0]]);
    scale = (
      <LabelWrapper>
        <LabelRatingContainer $labelColor={props.labelColor}>
          {!props.onlyText && (
            <LabelDisplay
              $empty={props.rating === null}
              $colorCount={props.maxRating}
              $selectedColorIndex={selectedAnswerIndex[0]}
            />
          )}
          {props.labels[selectedAnswerIndex[0]] ? (
            <LabelContent $onlyText={props.onlyText} $small={props.small}>
              {props.rating !== null ? labelContent : '...'}
            </LabelContent>
          ) : null}
        </LabelRatingContainer>
      </LabelWrapper>
    );
  }
  // Options
  if (props.type === ScaleVisualization.McqOptions) {
    scale = (
      <LabelWrapper>
        <LabelRatingContainer $labelColor={props.labelColor}>
          <McqAnswerContainer $showList={Array.isArray(props.selectedLabelIndex)}>
            {visibleOptions.map(index => (
              <li key={index}>
                <LabelContent $onlyText={props.onlyText} $small={props.small}>
                  {props.rating !== null ? translateContent(props.labels[index]) : '...'}
                </LabelContent>
              </li>
            ))}
          </McqAnswerContainer>
          {canViewMore && (
            <McqAnchorStyled onClick={showHideVisibleOptions}>
              {visibleOptions.length !== selectedAnswerIndex.length
                ? t('x.reports.answers.mcq.show.more', { optionCount: selectedAnswerIndex.length - 1 })
                : t('x.reports.answers.mcq.show.less')}
            </McqAnchorStyled>
          )}
        </LabelRatingContainer>
      </LabelWrapper>
    );
  }

  return scale;
}

export default DisplayScale;
